import { Columns2, RotateCcw } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { CircleLoader } from 'common-src/components/base';
import { ColorsNew } from 'common-src/styles';

import { VISIT_TYPES, WORKFLOW_SCOPE } from '../../constants';
import {
  dayName,
  daysUntil,
  EASTERN_TIMEZONE,
  filterApptsByDateForScope,
  getAppointmentEasternDayOffset,
  getAppointmentReadinessStatus,
  isTaskCanceledForReadiness,
  monthDay,
  parseReadinessCalendarDate,
} from '../../helpers';
import { departmentsInScope, hasActiveScopeFilters } from '../../scope';
import AppointmentCard from '../AppointmentCard';
import { HuddleNotesPanel } from '../HuddleNotes';
import ReadinessDetail from '../ReadinessDetail';
import ScopeFilterGroup from '../ScopeFilterGroup';
import TaskDetail from '../TaskDetail';
import styles from './ReadinessView.module.scss';

const defaultAppointmentTaskLookupKey = (appointment) => `${appointment?.id || ''}`.trim();

const ReadinessView = ({
  isLoading,
  workflowScope,
  onChangeWorkflowScope,
  apptsByDate,
  postVisitIsLoading,
  postApptsByDate,
  postVisitAppointmentTasksById = {},
  pinnedTeam = null,
  onTogglePinnedTeam,
  departments = [],
  departmentLabelById = {},
  scopeFilters,
  onChangeScopeFilters,
  guideEnabled = false,
  onToggleGuide,
  isScopePaused = false,
  onToggleScopePause,
  isPinnedToCurrentScope = false,
  canResetToPinnedScope = false,
  showScopeUtilities = false,
  onToggleScopePin,
  onResetScopeToPinned,
  selectedPostVisitAppt,
  setSelectedPostVisitAppt,
  dismissedPostVisitCount = 0,
  onDismissPostVisitAppt,
  onRestoreAllPostVisitAppts,
  selectedAppt,
  taskFromReadiness,
  appointmentTasksById,
  notesByApptId = {},
  onPostHuddleNote,
  onEditHuddleNote,
  getAppointmentTaskLookupKey = defaultAppointmentTaskLookupKey,
  setSelectedAppt,
  setTaskFromReadiness,
  onMarkComplete,
  markingTaskId,
  onCancelTask,
  cancelingTaskId,
  onReassignTask,
  reassigningTaskId,
  onFollowTask,
  followingTaskId,
  onUnfollowTask,
  unfollowingTaskId,
  onAssignToMe,
  assigningTaskId,
  onUnassignTask,
  unassigningTaskId,
  onBlockTask,
  blockingTaskId,
  onUnblockTask,
  unblockingTaskId,
  onEscalateTask,
  escalatingTaskId,
  onDeescalateTask,
  deescalatingTaskId,
  onDelegateSubtasks,
  delegatingSubtasksTaskId,
  onCancelSubtask,
  cancelingSubtaskId,
  currentUserId,
  currentUserName,
  cortexOrigin,
  onReplySent,
  delegatedSubtasksByParentId,
}) => {
  const departmentDefinitions = departments;
  const scopedDepartmentIds = React.useMemo(() => {
    const scoped = departmentsInScope(departmentDefinitions, scopeFilters, guideEnabled);
    return new Set(scoped.map((department) => department.id));
  }, [departmentDefinitions, scopeFilters, guideEnabled]);
  const isAllScope = isScopePaused || (!hasActiveScopeFilters(scopeFilters) && !guideEnabled);
  const preFilteredResult = React.useMemo(
    () =>
      filterApptsByDateForScope(
        apptsByDate,
        { isAllScope, scopedDepartmentIds },
        { excludePastEasternDates: true },
      ),
    [apptsByDate, isAllScope, scopedDepartmentIds],
  );
  const {
    flattened: flattenedAppointments,
    filtered: filteredAppointments,
    filteredByDate: filteredApptsByDate,
    sortedDates,
  } = preFilteredResult;
  const departmentReadinessById = React.useMemo(
    () =>
      departmentDefinitions.reduce((acc, department) => {
        const departmentAppointments = flattenedAppointments.filter(
          (appointment) => `${appointment?.departmentAthenaId ?? ''}`.trim() === department.id,
        );

        if (!departmentAppointments.length) {
          acc[department.id] = 'default';
          return acc;
        }

        const hasRed = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'red',
        );
        const hasYellow = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'yellow',
        );
        const hasOrange = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(
              appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [],
            ) === 'orange',
        );

        acc[department.id] = hasRed ? 'red' : hasYellow ? 'yellow' : hasOrange ? 'orange' : 'green';
        return acc;
      }, {}),
    [appointmentTasksById, departmentDefinitions, flattenedAppointments],
  );
  const postFilteredResult = React.useMemo(
    () => filterApptsByDateForScope(postApptsByDate, { isAllScope, scopedDepartmentIds }),
    [postApptsByDate, isAllScope, scopedDepartmentIds],
  );
  const {
    filtered: postFilteredAppointments,
    filteredByDate: postFilteredApptsByDate,
    sortedDates: postSortedDates,
  } = postFilteredResult;
  const filteredReadinessStats = React.useMemo(
    () =>
      filteredAppointments.reduce(
        (acc, appointment) => {
          const appointmentTasks =
            appointmentTasksById[getAppointmentTaskLookupKey(appointment)] || [];
          const readinessStatus = getAppointmentReadinessStatus(appointmentTasks);

          acc.orange += appointmentTasks.filter((task) => isTaskCanceledForReadiness(task)).length;
          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, orange: 0, green: 0 },
      ),
    [appointmentTasksById, filteredAppointments],
  );
  const next14DayAppointments = filteredAppointments.filter((appointment) => {
    const dayOffset = getAppointmentEasternDayOffset(appointment);
    return dayOffset !== null && dayOffset >= 0 && dayOffset <= 14;
  }).length;

  React.useEffect(() => {
    const preFilteredIds = new Set(
      filteredAppointments.map((appointment) => `${appointment?.id || ''}`),
    );
    const postFilteredIds = new Set(
      postFilteredAppointments.map((appointment) => `${appointment?.id || ''}`),
    );
    const filteredSourceAppointmentIds = new Set(
      [...filteredAppointments, ...postFilteredAppointments].map(
        (appointment) => `${appointment?.sourceAppointmentId || appointment?.id || ''}`,
      ),
    );

    if (selectedAppt?.id && !preFilteredIds.has(`${selectedAppt.id}`)) {
      setSelectedAppt(null);
      setTaskFromReadiness(null);
      return;
    }

    if (selectedPostVisitAppt?.id && !postFilteredIds.has(`${selectedPostVisitAppt.id}`)) {
      setSelectedPostVisitAppt(null);
      setTaskFromReadiness(null);
      return;
    }

    if (
      taskFromReadiness?.externalObjectId &&
      !filteredSourceAppointmentIds.has(`${taskFromReadiness.externalObjectId}`)
    ) {
      setTaskFromReadiness(null);
    }
  }, [
    filteredAppointments,
    postFilteredAppointments,
    selectedAppt,
    selectedPostVisitAppt,
    setSelectedAppt,
    setSelectedPostVisitAppt,
    setTaskFromReadiness,
    taskFromReadiness,
  ]);

  const stats = [
    { key: 'upcoming', label: 'Next 14 days', value: next14DayAppointments, sub: 'visits' },
    { key: 'green', label: 'Ready', value: filteredReadinessStats.green, tone: 'green' },
    { key: 'orange', label: 'Canceled', value: filteredReadinessStats.orange, tone: 'orange' },
    { key: 'yellow', label: 'At Risk', value: filteredReadinessStats.yellow, tone: 'yellow' },
    { key: 'red', label: 'Not Ready', value: filteredReadinessStats.red, tone: 'red' },
  ];
  const isPostVisitTab = workflowScope === WORKFLOW_SCOPE.POST_VISIT;
  const isSplitView = workflowScope === WORKFLOW_SCOPE.SPLIT;
  const showWorkflowScopeCounts = !isLoading && !postVisitIsLoading;

  const detailAppt = isPostVisitTab
    ? selectedPostVisitAppt
    : selectedAppt || (isSplitView ? selectedPostVisitAppt : null);
  const detailIsPostVisit =
    isPostVisitTab || (isSplitView && !selectedAppt && !!selectedPostVisitAppt);
  const detailTasksById = detailIsPostVisit ? postVisitAppointmentTasksById : appointmentTasksById;
  const [notesOpenForApptId, setNotesOpenForApptId] = React.useState(null);
  const isHuddleNotesOpen = !!detailAppt && notesOpenForApptId === `${detailAppt.id}`;
  const clearDetailSelection = () => {
    setNotesOpenForApptId(null);
    setSelectedAppt(null);
    setSelectedPostVisitAppt(null);
  };
  const selectPreVisitAppt = (appt) => {
    setNotesOpenForApptId(null);
    setSelectedPostVisitAppt(null);
    setSelectedAppt(appt);
  };
  const selectPostVisitAppt = (appt) => {
    setNotesOpenForApptId(null);
    setSelectedAppt(null);
    setSelectedPostVisitAppt(appt);
  };

  const readinessAppointmentLabel = detailAppt
    ? `${detailAppt?.patient?.name || 'Appointment'} · ${
        VISIT_TYPES[detailAppt?.visitType]?.label || detailAppt?.visitType || ''
      }`
    : '';
  const readinessStatus = detailAppt
    ? getAppointmentReadinessStatus(detailTasksById[getAppointmentTaskLookupKey(detailAppt)] || [])
    : 'default';

  const todayLabel = `Today · ${monthDay(new Date())}`;

  const renderApptColumn = ({
    columnIsLoading,
    columnSortedDates,
    columnApptsByDate,
    columnTasksById,
    columnSelectedAppt,
    columnIsPostVisit,
    onSelectAppt,
  }) => (
    <>
      <div className={styles.todayDivider}>
        <div className={styles.todayDividerLine} />
        <span className={styles.todayDividerLabel}>
          <span className={styles.todayDividerDot} />
          {todayLabel}
        </span>
      </div>
      {columnIsLoading && (
        <div className={styles.loaderWrap}>
          <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
        </div>
      )}
      {!columnIsLoading && !columnSortedDates.length && (
        <div className={styles.emptyFilterState}>
          {isAllScope ? 'No appointments available.' : 'No appointments for the selected scope.'}
        </div>
      )}
      {!columnIsLoading &&
        columnSortedDates.map((date) => {
          const appointments = columnApptsByDate[date] || [];
          const dateObj = parseReadinessCalendarDate(date);
          const isValidDate = Boolean(dateObj);
          const dayOffset = isValidDate ? daysUntil(date, EASTERN_TIMEZONE) : null;
          const isToday = dayOffset === 0;
          const dayLabel = isValidDate ? dayName(dateObj) : '—';
          const dayNumber = isValidDate ? dateObj.getDate() : '—';
          const monthLabel = isValidDate ? monthDay(dateObj) : '—';

          return (
            <div key={date} className={styles.dateGroup}>
              <div className={styles.dateHeader}>
                <div className={styles.dateLeft}>
                  <div
                    className={`${styles.dateDayName} ${isToday ? styles.dateToday : styles.dateMuted}`}
                  >
                    {dayLabel}
                  </div>
                  <div
                    className={`${styles.dateDayNumber} ${isToday ? styles.dateToday : styles.dateNormal}`}
                  >
                    {dayNumber}
                  </div>
                </div>
                <div className={styles.dateDivider} />
                <span className={styles.dateMeta}>
                  {monthLabel}
                  {isToday ? ' · Today' : ''}
                </span>
              </div>
              <div className={styles.cardsGrid}>
                {appointments.map((appt) => (
                  <AppointmentCard
                    key={appt.id}
                    appt={appt}
                    isSelected={columnSelectedAppt?.id === appt.id}
                    appointmentTasks={columnTasksById[getAppointmentTaskLookupKey(appt)] || []}
                    isPostVisit={columnIsPostVisit}
                    departmentLabelById={departmentLabelById}
                    huddleNoteCount={notesByApptId[appt.id]?.length || 0}
                    onDismiss={columnIsPostVisit ? onDismissPostVisitAppt : undefined}
                    onClick={() => {
                      onSelectAppt(appt);
                      setTaskFromReadiness(null);
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      {columnIsPostVisit && dismissedPostVisitCount > 0 && (
        <button
          type="button"
          className={styles.restoreAllButton}
          onClick={onRestoreAllPostVisitAppts}
        >
          <RotateCcw size={12} className={styles.restoreAllIcon} />
          {`${dismissedPostVisitCount} hidden · Restore all`}
        </button>
      )}
    </>
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <ScopeFilterGroup
          departments={departmentDefinitions}
          filters={scopeFilters}
          onChangeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          departmentToneById={departmentReadinessById}
          showUtilities={showScopeUtilities}
          isPinned={isPinnedToCurrentScope}
          onTogglePin={onToggleScopePin}
          canReset={canResetToPinnedScope}
          onReset={onResetScopeToPinned}
          isPaused={isScopePaused}
          onTogglePause={onToggleScopePause}
        />
        <div className={styles.viewModeGroup} role="group" aria-label="Visit view mode">
          <button
            type="button"
            aria-pressed={workflowScope === WORKFLOW_SCOPE.PRE_VISIT}
            onClick={() => onChangeWorkflowScope?.(WORKFLOW_SCOPE.PRE_VISIT)}
            className={`${styles.viewModeButton} ${
              workflowScope === WORKFLOW_SCOPE.PRE_VISIT ? styles.viewModeButtonActive : ''
            }`}
          >
            Pre-visit
            {showWorkflowScopeCounts && (
              <span className={styles.viewModeCount}>{filteredAppointments.length}</span>
            )}
          </button>
          <button
            type="button"
            aria-pressed={workflowScope === WORKFLOW_SCOPE.POST_VISIT}
            onClick={() => onChangeWorkflowScope?.(WORKFLOW_SCOPE.POST_VISIT)}
            className={`${styles.viewModeButton} ${
              workflowScope === WORKFLOW_SCOPE.POST_VISIT ? styles.viewModeButtonActive : ''
            }`}
          >
            Post-visit
            {showWorkflowScopeCounts && (
              <span className={styles.viewModeCount}>{postFilteredAppointments.length}</span>
            )}
          </button>
          <button
            type="button"
            aria-pressed={isSplitView}
            onClick={() => onChangeWorkflowScope?.(WORKFLOW_SCOPE.SPLIT)}
            className={`${styles.viewModeButton} ${isSplitView ? styles.viewModeButtonActive : ''}`}
          >
            <Columns2 size={14} className={styles.viewModeIcon} />
            Side by side
          </button>
        </div>
      </div>

      {!isPostVisitTab && (
        <div className={styles.statsRow}>
          {stats.map((stat) => (
            <div key={stat.key} className={styles.statCard}>
              <div className={styles.statLabel}>{stat.label}</div>
              <div className={styles.statValueRow}>
                <span
                  className={`${styles.statValue} ${
                    stat.tone ? styles[`statValue${stat.tone}`] : ''
                  }`}
                >
                  {stat.value}
                </span>
                {stat.sub && <span className={styles.statSub}>{stat.sub}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className={`${styles.layout} ${detailAppt || taskFromReadiness ? styles.layoutWithDetail : ''}`}
      >
        <div className={styles.listColumn}>
          {isSplitView ? (
            <div className={styles.splitGrid}>
              <div className={styles.splitColumn}>
                <div className={styles.splitColumnHeader}>
                  <span className={styles.splitColumnLabel}>Pre-visit readiness</span>
                  <span className={styles.splitColumnCount}>
                    {sortedDates.reduce(
                      (count, date) => count + (filteredApptsByDate[date]?.length || 0),
                      0,
                    )}
                  </span>
                </div>
                {renderApptColumn({
                  columnIsLoading: isLoading,
                  columnSortedDates: sortedDates,
                  columnApptsByDate: filteredApptsByDate,
                  columnTasksById: appointmentTasksById,
                  columnSelectedAppt: selectedAppt,
                  onSelectAppt: selectPreVisitAppt,
                })}
              </div>
              <div className={styles.splitColumn}>
                <div className={styles.splitColumnHeader}>
                  <span className={styles.splitColumnLabel}>Post-visit follow-up</span>
                  <span className={styles.splitColumnCount}>
                    {postSortedDates.reduce(
                      (count, date) => count + (postFilteredApptsByDate[date]?.length || 0),
                      0,
                    )}
                  </span>
                </div>
                {renderApptColumn({
                  columnIsLoading: postVisitIsLoading,
                  columnSortedDates: postSortedDates,
                  columnApptsByDate: postFilteredApptsByDate,
                  columnTasksById: postVisitAppointmentTasksById,
                  columnSelectedAppt: selectedPostVisitAppt,
                  columnIsPostVisit: true,
                  onSelectAppt: selectPostVisitAppt,
                })}
              </div>
            </div>
          ) : (
            renderApptColumn({
              columnIsLoading: isPostVisitTab ? postVisitIsLoading : isLoading,
              columnSortedDates: isPostVisitTab ? postSortedDates : sortedDates,
              columnApptsByDate: isPostVisitTab ? postFilteredApptsByDate : filteredApptsByDate,
              columnTasksById: isPostVisitTab
                ? postVisitAppointmentTasksById
                : appointmentTasksById,
              columnSelectedAppt: isPostVisitTab ? selectedPostVisitAppt : selectedAppt,
              columnIsPostVisit: isPostVisitTab,
              onSelectAppt: isPostVisitTab ? selectPostVisitAppt : selectPreVisitAppt,
            })
          )}
        </div>
        {(detailAppt || taskFromReadiness) && (
          <div className={styles.detailPanel}>
            <div className={styles.detailPanelInner}>
              {taskFromReadiness ? (
                <TaskDetail
                  task={taskFromReadiness}
                  relatedTasks={
                    detailAppt
                      ? detailTasksById[getAppointmentTaskLookupKey(detailAppt)] || []
                      : undefined
                  }
                  cachedDelegatedSubtasks={
                    taskFromReadiness?.parentTaskId
                      ? undefined
                      : delegatedSubtasksByParentId?.[`${taskFromReadiness.id}`] || []
                  }
                  onClose={() => setTaskFromReadiness(null)}
                  onBackToReadiness={() => setTaskFromReadiness(null)}
                  readinessAppointmentLabel={readinessAppointmentLabel}
                  readinessStatus={readinessStatus}
                  onAssignToMe={onAssignToMe}
                  isAssigning={assigningTaskId?.has(taskFromReadiness.id)}
                  onUnassign={onUnassignTask}
                  isUnassigning={unassigningTaskId === taskFromReadiness.id}
                  onMarkComplete={onMarkComplete}
                  isMarking={markingTaskId === taskFromReadiness.id}
                  onCancelTask={onCancelTask}
                  isCanceling={cancelingTaskId === taskFromReadiness.id}
                  onReassign={onReassignTask}
                  isReassigning={reassigningTaskId === taskFromReadiness.id}
                  onFollowTask={onFollowTask}
                  isFollowing={followingTaskId === taskFromReadiness.id}
                  onUnfollowTask={onUnfollowTask}
                  isUnfollowing={unfollowingTaskId === taskFromReadiness.id}
                  onBlockTask={onBlockTask}
                  isBlocking={blockingTaskId === taskFromReadiness.id}
                  onUnblockTask={onUnblockTask}
                  isUnblocking={unblockingTaskId === taskFromReadiness.id}
                  onEscalateTask={onEscalateTask}
                  isEscalating={escalatingTaskId === taskFromReadiness.id}
                  onDeescalateTask={onDeescalateTask}
                  isDeescalating={deescalatingTaskId === taskFromReadiness.id}
                  onDelegateSubtasks={onDelegateSubtasks}
                  isDelegatingSubtasks={delegatingSubtasksTaskId === taskFromReadiness.id}
                  onCancelSubtask={onCancelSubtask}
                  cancelingSubtaskId={cancelingSubtaskId}
                  currentUserId={currentUserId}
                  currentUserName={currentUserName}
                  cortexOrigin={cortexOrigin}
                  onReplySent={onReplySent}
                  onSelectTask={setTaskFromReadiness}
                />
              ) : isHuddleNotesOpen ? (
                <HuddleNotesPanel
                  backLabel={
                    detailIsPostVisit ? 'Back to post-visit follow-up' : 'Back to readiness'
                  }
                  title={readinessAppointmentLabel}
                  status={readinessStatus}
                  notes={notesByApptId[detailAppt.id] || []}
                  onBack={() => setNotesOpenForApptId(null)}
                  onPostNote={(text) =>
                    onPostHuddleNote(detailAppt, text, detailIsPostVisit ? 'post-visit' : 'prepare')
                  }
                  onEditNote={onEditHuddleNote}
                />
              ) : (
                <ReadinessDetail
                  appt={detailAppt}
                  onClose={clearDetailSelection}
                  onSelectTask={setTaskFromReadiness}
                  appointmentTasks={detailTasksById[getAppointmentTaskLookupKey(detailAppt)] || []}
                  delegatedSubtasksByParentId={delegatedSubtasksByParentId}
                  isPostVisit={detailIsPostVisit}
                  departmentLabelById={departmentLabelById}
                  onDismiss={onDismissPostVisitAppt}
                  pinnedTeam={pinnedTeam}
                  onTogglePinnedTeam={onTogglePinnedTeam}
                  huddleNotes={notesByApptId[detailAppt.id] || []}
                  onOpenHuddleNotes={() => setNotesOpenForApptId(`${detailAppt.id}`)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ReadinessView.propTypes = {
  isLoading: PropTypes.bool,
  workflowScope: PropTypes.string,
  onChangeWorkflowScope: PropTypes.func,
  apptsByDate: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  pinnedTeam: PropTypes.string,
  onTogglePinnedTeam: PropTypes.func,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      regionName: PropTypes.string,
      countyName: PropTypes.string,
      marketName: PropTypes.string,
    }),
  ),
  departmentLabelById: PropTypes.object,
  scopeFilters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string).isRequired,
    counties: PropTypes.arrayOf(PropTypes.string).isRequired,
    markets: PropTypes.arrayOf(PropTypes.string).isRequired,
    departments: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onChangeScopeFilters: PropTypes.func.isRequired,
  guideEnabled: PropTypes.bool,
  onToggleGuide: PropTypes.func,
  isScopePaused: PropTypes.bool,
  onToggleScopePause: PropTypes.func,
  isPinnedToCurrentScope: PropTypes.bool,
  canResetToPinnedScope: PropTypes.bool,
  showScopeUtilities: PropTypes.bool,
  onToggleScopePin: PropTypes.func,
  onResetScopeToPinned: PropTypes.func,
  postVisitIsLoading: PropTypes.bool,
  postApptsByDate: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  postVisitAppointmentTasksById: PropTypes.object,
  selectedPostVisitAppt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    visitType: PropTypes.string,
  }),
  setSelectedPostVisitAppt: PropTypes.func,
  dismissedPostVisitCount: PropTypes.number,
  onDismissPostVisitAppt: PropTypes.func,
  onRestoreAllPostVisitAppts: PropTypes.func,
  selectedAppt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    visitType: PropTypes.string,
  }),
  taskFromReadiness: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    externalObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  appointmentTasksById: PropTypes.object,
  notesByApptId: PropTypes.object,
  onPostHuddleNote: PropTypes.func,
  onEditHuddleNote: PropTypes.func,
  getAppointmentTaskLookupKey: PropTypes.func,
  setSelectedAppt: PropTypes.func,
  setTaskFromReadiness: PropTypes.func,
  onMarkComplete: PropTypes.func,
  markingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelTask: PropTypes.func,
  cancelingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReassignTask: PropTypes.func,
  reassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFollowTask: PropTypes.func,
  followingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnfollowTask: PropTypes.func,
  unfollowingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAssignToMe: PropTypes.func,
  assigningTaskId: PropTypes.instanceOf(Set),
  onUnassignTask: PropTypes.func,
  unassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlockTask: PropTypes.func,
  blockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnblockTask: PropTypes.func,
  unblockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEscalateTask: PropTypes.func,
  escalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeescalateTask: PropTypes.func,
  deescalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDelegateSubtasks: PropTypes.func,
  delegatingSubtasksTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelSubtask: PropTypes.func,
  cancelingSubtaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  cortexOrigin: PropTypes.string,
  onReplySent: PropTypes.func,
  delegatedSubtasksByParentId: PropTypes.object,
};

export default ReadinessView;
